/* Dashboard Builder — default seed layouts + ready-made starter templates.
 *
 * DEFAULT_SEED mirrors the built-in default Dashboard / Analytics pages as
 * editable tiles, so opening the builder for the first time (no saved layout)
 * lets the user rearrange and edit the EXISTING tiles instead of starting
 * from a blank canvas.
 *
 * STARTER_TEMPLATES are one-click boards an admin can drop in and tweak. */
import { TileInstance, DashPage } from './types';

export type SeedTile = Omit<TileInstance, 'i'>;

let _n = 0;
export const materialize = (tiles: SeedTile[]): TileInstance[] =>
  tiles.map((t) => ({ ...t, i: `${t.type}-${Date.now().toString(36)}-${(_n++).toString(36)}` }));

export const DEFAULT_SEED: Record<DashPage, SeedTile[]> = {
  dashboard: [
    { type: 'operational_kpis', title: 'Operational KPIs', x: 0, y: 0, w: 12, h: 2 },
    { type: 'risk_distribution', title: 'Risk distribution', chart: 'donut', x: 0, y: 2, w: 5, h: 4 },
    { type: 'outstanding', title: 'Outstanding actions', x: 5, y: 2, w: 3, h: 4 },
    { type: 'top_sites', title: 'Top risk sites', chart: 'table', x: 8, y: 2, w: 4, h: 5 },
    { type: 'broker_portfolio', title: 'Broker portfolio', chart: 'stacked', x: 0, y: 6, w: 8, h: 4 },
  ],
  analytics: [
    { type: 'kpi', title: 'Sites assessed', metric: 'sites', x: 0, y: 0, w: 3, h: 2 },
    { type: 'kpi', title: 'Total cash exposure', metric: 'total_cash', x: 3, y: 0, w: 3, h: 2 },
    { type: 'kpi', title: 'Avg cash / site', metric: 'avg_cash', x: 6, y: 0, w: 2, h: 2 },
    { type: 'kpi', title: 'Avg risk score', metric: 'avg_score', x: 8, y: 0, w: 2, h: 2 },
    { type: 'kpi', title: 'High / Critical', metric: 'high_critical', x: 10, y: 0, w: 2, h: 2 },
    { type: 'site_map', title: 'Site location map', chart: 'map', x: 0, y: 2, w: 8, h: 5 },
    { type: 'risk_distribution', title: 'Risk distribution', chart: 'donut', x: 8, y: 2, w: 4, h: 5 },
    { type: 'top_sites', title: 'Top risk sites', chart: 'table', x: 0, y: 7, w: 6, h: 4 },
    { type: 'cash_vs_score', title: 'Cash vs risk score', chart: 'scatter', x: 6, y: 7, w: 6, h: 4 },
    { type: 'abm_types', title: 'ABM type analysis', chart: 'stacked', x: 0, y: 11, w: 6, h: 4 },
    { type: 'worst_categories', title: 'Worst answer categories', chart: 'hbar', x: 6, y: 11, w: 6, h: 4 },
    { type: 'security_focus', title: 'Security pass rate', x: 0, y: 15, w: 12, h: 4 },
  ],
};

export interface StarterTemplate {
  key: string; label: string; description: string; tiles: SeedTile[];
}

export const STARTER_TEMPLATES: StarterTemplate[] = [
  {
    key: 'executive_summary',
    label: 'Executive summary',
    description: 'Headline KPIs, risk distribution, top risk sites and outstanding actions.',
    tiles: [
      { type: 'operational_kpis', title: 'Operational KPIs', x: 0, y: 0, w: 12, h: 2 },
      { type: 'risk_distribution', title: 'Risk distribution', chart: 'donut', x: 0, y: 2, w: 5, h: 4 },
      { type: 'top_sites', title: 'Top risk sites', chart: 'table', x: 5, y: 2, w: 7, h: 4 },
      { type: 'outstanding', title: 'Outstanding actions', x: 0, y: 6, w: 5, h: 4 },
    ],
  },
  {
    key: 'broker_review',
    label: 'Broker review',
    description: 'Broker portfolio breakdown, per-broker risk bands and top sites.',
    tiles: [
      { type: 'broker_portfolio', title: 'Broker risk bands', chart: 'stacked', x: 0, y: 0, w: 7, h: 5 },
      { type: 'risk_distribution', title: 'Risk distribution', chart: 'bar', x: 7, y: 0, w: 5, h: 5 },
      { type: 'broker_portfolio', title: 'Broker portfolio table', chart: 'table', x: 0, y: 5, w: 6, h: 4 },
      { type: 'top_sites', title: 'Top risk sites', chart: 'table', x: 6, y: 5, w: 6, h: 4 },
    ],
  },
  {
    key: 'geographic_risk',
    label: 'Geographic risk',
    description: 'Site location map, risk distribution and cash-vs-score scatter.',
    tiles: [
      { type: 'site_map', title: 'Site location map', chart: 'map', x: 0, y: 0, w: 8, h: 6 },
      { type: 'risk_distribution', title: 'Risk distribution', chart: 'donut', x: 8, y: 0, w: 4, h: 6 },
      { type: 'cash_vs_score', title: 'Cash vs risk score', chart: 'scatter', x: 0, y: 6, w: 12, h: 4 },
    ],
  },
];
